import React from "react";
import { Button } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";

interface CustomBtnProps {
  action: () => void;
  type?: string;
  icon?: React.ReactNode;
  tip?: string;
  children?: React.ReactNode;
  className?: string;
}

function CustomBtn(props: CustomBtnProps): React.JSX.Element {
  const { action, type, icon, tip, children, className } = props;

  // Map react-bootstrap variants to MUI variants
  const getMuiVariant = (
    bootstrapVariant?: string,
  ): "text" | "outlined" | "contained" => {
    const variantMap: { [key: string]: "text" | "outlined" | "contained" } = {
      primary: "contained",
      secondary: "outlined",
      success: "contained",
      danger: "contained",
      warning: "contained",
      info: "contained",
      light: "outlined",
      dark: "contained",
      link: "text",
      "outline-primary": "outlined",
      "outline-secondary": "outlined",
      "outline-success": "outlined",
      "outline-danger": "outlined",
      "outline-warning": "outlined",
      "outline-info": "outlined",
      "outline-light": "outlined",
      "outline-dark": "outlined",
    };

    return variantMap[bootstrapVariant || ""] || "contained";
  };

  // Map react-bootstrap color variants to MUI colors
  const getMuiColor = (
    bootstrapVariant?: string,
  ):
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning" => {
    const colorMap: {
      [key: string]:
        | "inherit"
        | "primary"
        | "secondary"
        | "success"
        | "error"
        | "info"
        | "warning";
    } = {
      primary: "primary",
      secondary: "secondary",
      success: "success",
      danger: "error",
      warning: "warning",
      info: "info",
      light: "inherit",
      dark: "inherit",
      link: "primary",
      "outline-primary": "primary",
      "outline-secondary": "secondary",
      "outline-success": "success",
      "outline-danger": "error",
      "outline-warning": "warning",
      "outline-info": "info",
      "outline-light": "inherit",
      "outline-dark": "inherit",
    };

    return colorMap[bootstrapVariant || ""] || "primary";
  };

  // Render button with or without tooltip based on whether tip is provided
  const renderButton = () => (
    <Button
      className={className}
      variant={getMuiVariant(type)}
      color={getMuiColor(type)}
      onClick={action}
      startIcon={icon}
      sx={{
        paddingLeft: "12px",
        paddingRight: "12px",
        minWidth: "auto",
        "& .MuiButton-startIcon": {
          marginLeft: 0,
          marginRight: icon && children ? "8px" : 0,
        },
      }}
    >
      {children}
    </Button>
  );

  // Only wrap with Tooltip if tip is provided
  if (tip) {
    return (
      <Tooltip title={tip} placement="top">
        {renderButton()}
      </Tooltip>
    );
  }

  return renderButton();
}

export default CustomBtn;
